import React from 'react';
import { useAppDispatch } from '../../hooks/redux';
import {
  addStockerPort,
  removeStockerPort,
  selectLayoutPort,
  setStockerPortEdit,
  updateStockerPort,
  getElementPorts,
  portParentTypeForElement,
} from '../../features/layout/layoutSlice';
import {
  addPort,
  removePort,
  selectEquipmentPort,
  setPortEditInstance,
  updatePort,
} from '../../features/equipment/equipmentSlice';
import {
  makePort,
  nextPortIndex,
  canRemovePort,
  defaultPortKindToAdd,
  defaultPortDisplayName,
  defaultInventoryLevelForColumn,
  formatPortOptionLabel,
  labelBayType,
  labelColumnKind,
  labelPortKind,
  portKindForColumnKind,
  resolveBayType,
  resolveColumnKind,
  resolvePortKind,
  portColor,
  BAY_TYPES,
  EQUIPMENT_PORT_KINDS,
  STOCKER_COLUMN_KINDS,
  STOCKER_PORT_KINDS,
  type BayType,
  type Port,
  type PortKind,
  type PortParentType,
  type StockerColumnKind,
} from '../../utils/ports';
import type { LayoutElement } from '../../types';
import type { EquipmentInstance } from '../../features/equipment/equipmentTypes';

type LayoutProps = {
  kind: 'layout';
  element: LayoutElement;
  editing: boolean;
};

type EquipmentProps = {
  kind: 'equipment';
  instance: EquipmentInstance;
  ports: Port[];
  editing: boolean;
};

type Props = LayoutProps | EquipmentProps;

function parentTypeForProps(props: Props): PortParentType {
  if (props.kind === 'layout') return portParentTypeForElement(props.element);
  return props.instance.objectType === 'stocker' ? 'stocker' : 'equipment';
}

/**
 * Shared Load Ports controls for layout stockers and equipment overlay
 * instances. Every dock can be added, retyped, dragged on canvas and removed,
 * down to the last one each object must keep.
 */
const LoadPortsEditor: React.FC<Props> = (props) => {
  const dispatch = useAppDispatch();
  const ports = props.kind === 'layout' ? getElementPorts(props.element) : props.ports;
  const editing = props.editing;
  const parentType = parentTypeForProps(props);
  const kindOptions = parentType === 'stocker' ? STOCKER_PORT_KINDS : EQUIPMENT_PORT_KINDS;
  // Tools are always intrabay, so only stocker docks expose the bay selector.
  const canEditBayType = parentType === 'stocker';
  const removable = canRemovePort(ports);

  const onAdd = () => {
    const portKind = defaultPortKindToAdd(parentType, ports);
    const slot = nextPortIndex(ports);
    if (props.kind === 'layout') {
      const parentName = props.element.metadata.name;
      const toolType = String(props.element.customProperties?.toolType ?? '').trim();
      dispatch(
        addStockerPort({
          elementId: props.element.id,
          port: {
            portKind,
            displayName: defaultPortDisplayName({
              parentName,
              toolType,
              portKind,
              fallbackName: `Port ${slot}`,
            }),
          },
        })
      );
      return;
    }
    dispatch(
      addPort({
        instanceId: props.instance.id,
        port: makePort({
          parentId: props.instance.id,
          parentType,
          name: `Port ${slot}`,
          displayName: defaultPortDisplayName({
            parentName: props.instance.name,
            toolType: props.instance.equipmentType,
            portKind,
            fallbackName: `Port ${slot}`,
          }),
          rx: 1,
          ry: 0.5,
          edge: 'right',
          portKind,
        }),
      })
    );
  };

  const onToggleEdit = () => {
    if (props.kind === 'layout') {
      dispatch(setStockerPortEdit(editing ? null : props.element.id));
      return;
    }
    dispatch(setPortEditInstance(editing ? null : props.instance.id));
  };

  const onRemove = (portId: string) => {
    if (!removable) return;
    if (props.kind === 'layout') {
      dispatch(removeStockerPort({ elementId: props.element.id, portId }));
      return;
    }
    dispatch(removePort({ instanceId: props.instance.id, portId }));
  };

  const onSelect = (portId: string) => {
    if (props.kind === 'layout') {
      dispatch(selectLayoutPort({ elementId: props.element.id, portId }));
      return;
    }
    dispatch(selectEquipmentPort({ instanceId: props.instance.id, portId }));
  };

  const patch = (portId: string, changes: Partial<Port>) => {
    if (props.kind === 'layout') {
      dispatch(updateStockerPort({ elementId: props.element.id, portId, changes }));
      return;
    }
    dispatch(
      updatePort({
        instanceId: props.instance.id,
        portId,
        changes,
        skipHistory: true,
      })
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>Load Ports: {ports.length}</span>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onAdd}
            className="font-medium text-blue-600 hover:text-blue-800"
          >
            + Add Port
          </button>
          <button
            type="button"
            onClick={onToggleEdit}
            className={`font-medium ${editing ? 'text-red-600' : 'text-blue-600'} hover:underline`}
          >
            {editing ? 'Done' : 'Drag on canvas'}
          </button>
        </div>
      </div>
      {ports.length > 0 && (
        <div className="space-y-1">
          {ports.map((p) => (
            <div
              key={p.id}
              className="space-y-1 text-xs text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded px-2 py-1.5"
            >
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => onSelect(p.id)}
                  title={`${formatPortOptionLabel(p)} — open port properties`}
                  className="truncate flex items-center min-w-0 hover:underline"
                >
                  <span
                    className="inline-block w-2 h-2 rounded-full mr-1.5 shrink-0"
                    style={{ backgroundColor: portColor(p, parentType) }}
                  />
                  <span className="truncate">{formatPortOptionLabel(p)}</span>
                </button>
                <button
                  type="button"
                  disabled={!removable}
                  title={
                    removable ? 'Remove port' : 'Each object must keep at least one port'
                  }
                  onClick={() => onRemove(p.id)}
                  className="text-red-400 hover:text-red-600 disabled:text-gray-300 disabled:cursor-not-allowed disabled:hover:text-gray-300 shrink-0"
                >
                  Remove
                </button>
              </div>
              <div className="flex items-center gap-1">
                <select
                  value={resolvePortKind(p, parentType)}
                  onChange={(e) => {
                    const next = e.target.value as PortKind;
                    if (
                      canEditBayType &&
                      (next === 'UNLOAD_RAW' || next === 'UNLOAD_FINISHED')
                    ) {
                      const nextColumn = next === 'UNLOAD_FINISHED' ? 'processed' : 'raw';
                      patch(p.id, {
                        portKind: next,
                        columnKind: nextColumn,
                        inventoryLevel: defaultInventoryLevelForColumn(nextColumn),
                      });
                      return;
                    }
                    patch(p.id, { portKind: next });
                  }}
                  className="input-field text-[10px] py-0.5 px-1 flex-1 min-w-0"
                  title="Port type"
                >
                  {kindOptions.map((option) => (
                    <option key={option} value={option}>
                      {labelPortKind(option)}
                    </option>
                  ))}
                </select>
                {canEditBayType ? (
                  <select
                    value={resolveBayType(p, parentType)}
                    onChange={(e) => patch(p.id, { bayType: e.target.value as BayType })}
                    className="input-field text-[10px] py-0.5 px-1 flex-1 min-w-0"
                    title="Bay type → portKind Interbay/Intrabay"
                  >
                    {BAY_TYPES.map((option) => (
                      <option key={option} value={option}>
                        {labelBayType(option)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="flex-1 text-[10px] text-gray-400 dark:text-gray-500 text-right pr-1">
                    Intrabay
                  </span>
                )}
              </div>
              {canEditBayType && (
                <select
                  value={resolveColumnKind(p, parentType) ?? 'raw'}
                  onChange={(e) => {
                    const next = e.target.value as StockerColumnKind;
                    patch(p.id, {
                      columnKind: next,
                      inventoryLevel: defaultInventoryLevelForColumn(next),
                      portKind: portKindForColumnKind(next),
                    });
                  }}
                  className="input-field text-[10px] py-0.5 px-1 w-full"
                  title="Column kind (raw / processed)"
                >
                  {STOCKER_COLUMN_KINDS.map((option) => (
                    <option key={option} value={option}>
                      {labelColumnKind(option)}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LoadPortsEditor;
